import { Search, Grid3x3, List } from "lucide-react"
import { Tabs, TabsList, TabsTab } from "@/components/ui/tabs"
import { Input } from "@/components/ui"
import { cn } from "@/utils/Functions"
import { ContactCard } from "./ContactCard"
import { ContactListItem } from "./ContactListItem"
import type { Contact, ContactFilter, ViewMode } from "../lib/data"

interface ContactsTabProps {
  contacts: Contact[]
  searchQuery: string
  onSearchChange: (value: string) => void
  contactFilter: ContactFilter
  onFilterChange: (filter: ContactFilter) => void
  viewMode: ViewMode
  onViewModeChange: (mode: ViewMode) => void
  onAction: (action: string) => void
}

export const ContactsTab = ({
  contacts,
  searchQuery,
  onSearchChange,
  contactFilter,
  onFilterChange,
  viewMode,
  onViewModeChange,
  onAction,
}: ContactsTabProps) => {
  return (
    <>
      <div className="px-6 py-3 border-b border-border flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Input
              size="lg"
              startIcon={<Search className="size-2 text-muted-foreground pointer-events-none" />}
              rounded="full"
              variant="muted"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search people..."
              aria-label="Search people"
            />
          </div>

          <Tabs value={contactFilter} onValueChange={(v) => onFilterChange(v as ContactFilter)}>
            <TabsList className="border border-border">
              {(["all", "network", "organization", "starred"] as ContactFilter[]).map((filter) => (
                <TabsTab key={filter} value={filter} className="text-xs capitalize">
                  {filter}
                </TabsTab>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <div className="flex items-center gap-1 p-1 rounded-lg bg-muted/50 border border-border">
          <button
            onClick={() => onViewModeChange("grid")}
            className={cn(
              "p-1.5 rounded-md",
              viewMode === "grid"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <Grid3x3 className="size-4" />
          </button>
          <button
            onClick={() => onViewModeChange("list")}
            className={cn(
              "p-1.5 rounded-md",
              viewMode === "list"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <List className="size-4" />
          </button>
        </div>
      </div>

      <div className="p-6">
        {contacts.length === 0 ? (
          <div className="h-full flex items-center justify-center py-12">
            <div className="text-center">
              <div className="size-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                <Search className="size-7 text-muted-foreground/40" />
              </div>
              <p className="text-foreground font-medium mb-1">No contacts found</p>
              <p className="text-muted-foreground text-sm">Try adjusting your search or filters</p>
            </div>
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
            {contacts.map((contact) => (
              <ContactCard key={contact.id} contact={contact} onAction={onAction} />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {contacts.map((contact) => (
              <ContactListItem key={contact.id} contact={contact} onAction={onAction} />
            ))}
          </div>
        )}
      </div>
    </>
  )
}
